// Produto de dentro de um kit com vários produtos
export interface Flavor {
  id: string;
  name: string;
  // Preço de cada unidade deste produto no kit
  price: number | null;
  // Preço promocional da unidade, válido no período de promoção do kit (null = sem promoção)
  promoPrice: number | null;
  // Quantidade disponível; null = sem controle de estoque
  stock: number | null;
  kitCategoryId?: string;
  isAvailable?: boolean;
}

// SINGLE: item unitário, com preço por unidade.
// PACK: kit de um único produto, com preço e quantidade de itens fixos (ex.: 100 coxinhas).
// KIT: kit com vários produtos, cada um com o seu preço; o cliente monta e o total vai somando.
export type ProductType = 'SINGLE' | 'PACK' | 'KIT';

export const PRODUCT_TYPE_LABELS: Record<ProductType, string> = {
  SINGLE: 'Item unitário',
  PACK: 'Kit unitário',
  KIT: 'Kit com vários produtos'
};

export interface Kit {
  id: string;
  name: string;
  type: ProductType;
  // Categoria do produto (null só nos produtos cadastrados antes das categorias)
  categoryId: string | null;
  description: string | null;
  // Preço da unidade (SINGLE) ou do kit (PACK). No KIT o preço é de cada produto.
  pricePer100: number;
  // Promoção com período: preço promocional do SINGLE/PACK (no KIT fica em cada produto) e as datas (ISO).
  // Fora do período vale o preço normal: ao chegar promoEndsAt a promoção some sozinha.
  promoPrice: number | null;
  promoStartsAt: string | null;
  promoEndsAt: string | null;
  // Unidades do kit de um único produto (1 nos outros tipos)
  itemsCount: number;
  // Estoque do SINGLE (unidades) ou do PACK (kits); null = sem controle. No KIT fica em cada produto.
  stock: number | null;
  // "Em breve": aparece na loja, mas ainda não pode ser comprado
  comingSoon: boolean;
  // Em até quantas parcelas o produto pode ser pago no cartão (dentro do parcelamento das formas de pagamento)
  maxInstallments: number;
  // URL absoluta da imagem de capa (null enquanto o produto não tem imagem)
  imageUrl: string | null;
  // Demais fotos do produto, na ordem da galeria
  images: ProductImage[];
  flavors: Flavor[];
}

// Foto da galeria do produto (além da capa)
export interface ProductImage {
  id: string;
  // URL absoluta
  url: string;
}

// Fotos extras que o produto pode ter além da capa (o mesmo limite da API)
export const MAX_GALLERY_IMAGES = 10;

// Todas as fotos do anúncio: a capa primeiro e depois a galeria
export function kitPhotos(kit: Kit): string[] {
  return [...(kit.imageUrl ? [kit.imageUrl] : []), ...kit.images.map(image => image.url)];
}

// Sem estoque: o item/kit unitário zerado, ou o kit em que todos os produtos zeraram
export function isOutOfStock(kit: Kit): boolean {
  if (kit.type !== 'KIT') return kit.stock === 0;
  return !kit.flavors.some(flavor => flavor.stock !== 0);
}

// A promoção vale do início (ou já, sem início) até o fim, sem incluir o fim (a mesma regra da API)
export function isPromoActive(kit: Pick<Kit, 'promoStartsAt' | 'promoEndsAt'>, now = Date.now()): boolean {
  if (!kit.promoEndsAt) return false;
  const starts = kit.promoStartsAt ? Date.parse(kit.promoStartsAt) : -Infinity;
  return starts <= now && now < Date.parse(kit.promoEndsAt);
}

// Preço promocional do item/kit unitário, só enquanto a promoção estiver valendo (null = preço normal)
export function kitPromoPrice(kit: Kit): number | null {
  return kit.type !== 'KIT' && kit.promoPrice !== null && isPromoActive(kit) ? kit.promoPrice : null;
}

// Preço cobrado agora do item/kit unitário
export function kitPrice(kit: Kit): number {
  return kitPromoPrice(kit) ?? kit.pricePer100;
}

// Preço promocional de um produto do kit, só enquanto a promoção do kit estiver valendo
export function flavorPromoPrice(kit: Kit, flavor: Flavor): number | null {
  return flavor.promoPrice !== null && flavor.promoPrice !== undefined && isPromoActive(kit) ? flavor.promoPrice : null;
}

// Preço cobrado agora de um produto do kit
export function flavorPrice(kit: Kit, flavor: Flavor): number {
  return flavorPromoPrice(kit, flavor) ?? flavor.price ?? 0;
}

// Algum preço em promoção agora (selo "Promoção" na vitrine)
export function hasActivePromo(kit: Kit): boolean {
  if (kit.type !== 'KIT') return kitPromoPrice(kit) !== null;
  return kit.flavors.some(flavor => flavorPromoPrice(kit, flavor) !== null);
}

// Último dia da oferta (o fim é o momento em que o preço normal volta, por isso o dia anterior)
export function promoLastDay(kit: Kit): Date | null {
  return hasActivePromo(kit) && kit.promoEndsAt ? new Date(Date.parse(kit.promoEndsAt) - 1) : null;
}

// "a partir de" do kit com vários produtos: o menor preço cobrado agora e o preço normal desse mesmo produto
export function kitStartingPrices(kit: Kit): { price: number; regular: number } | null {
  const options = kit.flavors
    .filter(f => f.stock !== 0 && f.price !== null)
    .map(f => ({ price: flavorPrice(kit, f), regular: f.price! }))
    .sort((a, b) => a.price - b.price);
  return options[0] ?? null;
}

// Menor preço cobrado agora entre os produtos do kit que ainda têm estoque ("a partir de")
export function kitStartingPrice(kit: Kit): number | null {
  return kitStartingPrices(kit)?.price ?? null;
}

// Payload esperado pelo POST /kits
export interface CreateKitPayload {
  name: string;
  categoryId: string;
  type: ProductType;
  description: string;
  pricePer100?: number;
  itemsCount?: number;
  stock?: number | null;
  // Promoção com período (null nos três = sem promoção). promoPrice só no item/kit unitário.
  promoPrice: number | null;
  promoStartsAt: string | null;
  promoEndsAt: string | null;
  // Produtos do kit com vários produtos (vazio nos outros tipos)
  flavors: { name: string; price: number; promoPrice: number | null; stock: number | null }[];
  comingSoon: boolean;
  maxInstallments: number;
  // Data URL da imagem de capa; omitida na edição mantém a atual
  image?: string;
  // Galeria na ordem de exibição: id de uma foto já salva (mantida) ou data URL de uma foto nova.
  // As fotos salvas que ficarem fora da lista são excluídas; omitida na edição mantém a galeria atual.
  gallery?: string[];
}

// Pode ir para o carrinho: nem "em breve" nem sem estoque
export function canBuy(kit: Kit): boolean {
  return !kit.comingSoon && !isOutOfStock(kit);
}
